import { useCallback, useState, type ReactNode } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { X, CheckCircle, HeartHandshake, Info } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import { type ContentBlock, getBlock } from "@src/packages/content";
import {
  useScrollHaptics,
  springs,
  SectionTitle,
  IconButton,
} from "@louise/moon-ui";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import InterestButton from "./InterestButton";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import FactSheetHeader, { type FactSheetHeaderBadge } from "./FactSheetHeader";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";
type Props = {
  item: ContentBlock<"program"> | null;
  onClose: () => void;
};

const MODULE_PROCEDURE_IDS: Record<string, string> = {
  "module-female-hormone-baseline": "proc-hormonal-testing",
  "module-ovarian-stimulation": "proc-ovarian-stimulation",
  "module-egg-retrieval": "proc-egg-retrieval",
  "module-embryo-transfer": "proc-embryo-transfer",
  "module-iui-procedure": "proc-iui",
  "module-pgt-a": "proc-embryo-biopsy-pgt",
  "module-genetic-carrier": "proc-carrier-testing",
  "module-tww-support": "proc-two-week-wait",
  "module-comprehensive-genomic": "proc-exome-genome-sequencing",
};

const MODULE_TEST_IDS: Record<string, string> = {
  "module-male-fertility-baseline": "test-semen-analysis",
};

function withoutEmDashes(value: string): string {
  return value.replace(/\s*\u2014\s*/g, " - ");
}

function ProgramSection({
  title,
  icon,
  children,
  description,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  description?: string;
}) {
  return (
    <View style={{ marginTop: 24 }}>
      <View style={{ marginBottom: description ? 6 : 10 }}>
        <SectionTitle icon={icon}>{title}</SectionTitle>
      </View>
      {description ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12.5,
            color: EMBER.colors.textLight,
            lineHeight: 17,
            marginBottom: 10,
            marginLeft: 4,
          }}
        >
          {withoutEmDashes(description)}
        </Text>
      ) : null}
      {children}
    </View>
  );
}

function ModuleIncludeRows({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 8 }}>
      {items.map((text, i) => (
        <View
          key={`${text}-${i}`}
          style={{
            flexDirection: "row",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <CheckCircle
            size={13}
            color={EMBER.colors.green}
            strokeWidth={2}
            style={{ marginTop: 2 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 12.5,
              color: EMBER.colors.textLight,
              lineHeight: 17,
            }}
          >
            {withoutEmDashes(text)}
          </Text>
        </View>
      ))}
    </View>
  );
}

const MODULE_TITLE_OVERRIDE_KEYS: Record<string, string> = {
  "module-female-hormone-baseline":
    "access.programs.module_title_overrides.female_hormone_baseline",
  "module-male-fertility-baseline":
    "access.programs.module_title_overrides.male_fertility_baseline",
  "module-female-optimization":
    "access.programs.module_title_overrides.female_optimization",
  "module-male-optimization":
    "access.programs.module_title_overrides.male_optimization",
};

function getModuleDisplayTitle(
  t: TranslationFunction,
  module: ContentBlock<"module">,
): string {
  const overrideKey = MODULE_TITLE_OVERRIDE_KEYS[module.id];
  return overrideKey ? t(overrideKey) : module.title;
}

function getPhaseTitle(title: string): string {
  return title.replace(/\s*\((optional|parallel)\)\s*/gi, "").trim();
}

function getProgramCategoryLabel(
  t: TranslationFunction,
  category: ContentBlock<"program">["body"]["category"],
) {
  if (category === "assessment") {
    return t("access.programs.detail_category.assessment");
  }
  if (category === "optimization") {
    return t("access.programs.detail_category.optimization");
  }
  if (category === "treatment") {
    return t("access.programs.detail_category.treatment");
  }
  if (category === "support") {
    return t("access.programs.detail_category.support");
  }
  return t("access.programs.detail_category.default");
}

function ProgramModuleDetailCard({
  module,
  onPress,
}: {
  module: ContentBlock<"module">;
  onPress?: (module: ContentBlock<"module">) => void;
}) {
  const t = useTranslations();
  const testId = MODULE_TEST_IDS[module.id];
  const procedureId = MODULE_PROCEDURE_IDS[module.id];
  const canOpenFactSheet = Boolean(onPress && (testId || procedureId));
  const body = module.body as ContentBlock<"module">["body"];
  const title = getModuleDisplayTitle(t, module);
  const shouldShowSubtitle = title === module.title;
  const capturePress = usePressCapture("ProgramModuleDetailCard");

  return (
    <Pressable
      disabled={!canOpenFactSheet}
      onPress={() => {
        capturePress(pressLabel(title), "open_program_module");
        onPress?.(module);
      }}
      style={({ pressed }) => ({
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.subtle,
        padding: 12,
        gap: 12,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
        {module.icon ? (
          <Text style={{ fontSize: 20 }}>{module.icon}</Text>
        ) : null}
        <View style={{ flex: 1, gap: 5 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 18,
              fontWeight: "800",
              color: EMBER.colors.text,
            }}
          >
            {withoutEmDashes(title)}
          </Text>
          {module.subtitle && shouldShowSubtitle ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12.5,
                lineHeight: 17,
                color: EMBER.colors.textLight,
              }}
            >
              {withoutEmDashes(module.subtitle)}
            </Text>
          ) : null}
        </View>
      </View>

      <ModuleIncludeRows items={body.includes} />
    </Pressable>
  );
}

function FallbackStepRows({
  steps,
}: {
  steps: NonNullable<ContentBlock<"program">["body"]["steps"]>;
}) {
  return (
    <View style={{ gap: 10 }}>
      {steps.map((step, i) => (
        <View
          key={`${step.title}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            padding: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: semanticColors.border.subtle,
            backgroundColor: semanticColors.surface.glass,
          }}
        >
          <View style={{ flex: 1, gap: 3 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "700",
                color: EMBER.colors.text,
                lineHeight: 18,
              }}
            >
              {withoutEmDashes(step.title)}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12.5,
                color: EMBER.colors.textLight,
                lineHeight: 17,
              }}
            >
              {withoutEmDashes(step.description)}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

export function ProgramDetailContent({
  item,
  onClose,
  skipExitAnimation,
  onOpenProcedure,
  onOpenTest,
}: {
  item: ContentBlock<"program">;
  onClose: () => void;
  skipExitAnimation?: boolean;
  onOpenProcedure?: (procedureId: string) => void;
  onOpenTest?: (testId: string) => void;
}) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const onScrollHaptics = useScrollHaptics();

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const scrollable = contentSize.height - layoutMeasurement.height;
      if (scrollable <= 0) {
        setScrollPercent(100);
        return;
      }
      setScrollPercent(
        Math.min(100, Math.round((contentOffset.y / scrollable) * 100)),
      );
    },
    [onScrollHaptics],
  );

  const { body } = item;
  const isComingSoon = body.status === "coming-soon";
  const hasPhases = body.phases && body.phases.length > 0;
  const categoryLabel = getProgramCategoryLabel(t, body.category);
  const headerBadges: FactSheetHeaderBadge[] = [
    { label: categoryLabel },
    ...(isComingSoon
      ? [{ label: t("learn.program_detail.badge_coming_soon") }]
      : []),
  ];

  const onModulePress = useCallback(
    (mod: ContentBlock<"module">) => {
      const testId = MODULE_TEST_IDS[mod.id];
      if (testId) {
        onOpenTest?.(testId);
        return;
      }

      const procedureId = MODULE_PROCEDURE_IDS[mod.id];
      if (procedureId) onOpenProcedure?.(procedureId);
    },
    [onOpenProcedure, onOpenTest],
  );

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <View
            style={{
              height: 3,
              backgroundColor: semanticColors.surface.muted,
              marginHorizontal: 16,
              borderRadius: 2,
            }}
          >
            <View
              style={{
                height: 3,
                width: `${scrollPercent}%`,
                backgroundColor: EMBER.colors.warm,
                borderRadius: 2,
              }}
            />
          </View>

          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor={semanticColors.surface.muted}
                  pressedBackgroundColor={semanticColors.surface.muted}
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              <FactSheetHeader
                title={item.title}
                subtitle={
                  item.subtitle ? withoutEmDashes(item.subtitle) : undefined
                }
                emoji={item.icon}
                badges={headerBadges}
              />

              {/* Phases */}
              {hasPhases
                ? body.phases!.map((phase) => {
                    const modules = phase.moduleIds
                      .map((id) => getBlock(id))
                      .filter(
                        (b): b is ContentBlock<"module"> =>
                          b !== undefined && b.kind === "module",
                      );

                    return (
                      <ProgramSection
                        key={phase.title}
                        title={getPhaseTitle(phase.title)}
                        icon={HeartHandshake}
                        description={phase.description}
                      >
                        <View style={{ gap: 10 }}>
                          {modules.map((mod) => (
                            <ProgramModuleDetailCard
                              key={mod.id}
                              module={mod}
                              onPress={
                                onOpenProcedure || onOpenTest
                                  ? onModulePress
                                  : undefined
                              }
                            />
                          ))}
                        </View>
                      </ProgramSection>
                    );
                  })
                : null}

              {/* Fallback: steps if no phases */}
              {!hasPhases && body.steps?.length ? (
                <ProgramSection
                  title={t("learn.program_detail.section_how_it_works")}
                  icon={Info}
                >
                  <FallbackStepRows steps={body.steps} />
                </ProgramSection>
              ) : null}

              {/* CTA */}
              {isComingSoon && (
                <View style={{ marginTop: 24 }}>
                  <InterestButton
                    featureId={item.id}
                    featureType="program"
                    featureName={item.title}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      color: EMBER.colors.textFaint,
                      textAlign: "center",
                      marginTop: 8,
                    }}
                  >
                    {t("learn.program_detail.notify_when_launches")}
                  </Text>
                </View>
              )}
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>
    </View>
  );
}

export default function ProgramDetailSheet({ item, onClose }: Props) {
  if (!item) {
    return null;
  }
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <ProgramDetailContent item={item} onClose={onClose} />
    </Modal>
  );
}
